import React, { useState, useEffect } from 'react';
import './UsersPage.css';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { getAuthToken, getUserRole } from '../Utils';
import { getPageTitle } from '../constants';

interface UserListItem {
  id: number;
  alias: string;
  firstName: string | null;
  lastName: string | null;
  seen: string | null;
  enabled?: boolean;
}

const UsersListPage: React.FC = () => {
  const { translations } = useOutletContext<{ translations: Record<string, string> }>();
  const [activeUsers, setActiveUsers] = useState<UserListItem[]>([]);
  const [inactiveUsers, setInactiveUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const isAdmin = getUserRole() === 'admin';

  useEffect(() => {
    document.title = getPageTitle('Users');
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const token = getAuthToken();
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const config = { headers, withCredentials: true };
        const activeResponse = await axios.get('/api/users?limit=100&offset=0&notActiveOnly=false', config);
        setActiveUsers(activeResponse.data);

        if (isAdmin) {
          const inactiveResponse = await axios.get('/api/users?limit=100&offset=0&notActiveOnly=true', config);
          setInactiveUsers(inactiveResponse.data);
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError(translations["error.fetchUsers"]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  const formatSeen = (seen: string | null): string => {
    if (!seen) return translations["user.neverSeen"];
    return new Date(seen).toLocaleDateString();
  };

  return (
    <div className="results-section">
      <h2>{translations["nav.users"]} ({showInactive ? activeUsers.length + inactiveUsers.length : activeUsers.length})</h2>
      {isAdmin && (
        <label className="inactive-toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          <span>{translations["users.showInactive"]}</span>
        </label>
      )}
      {loading ? (
        <p className="no-results">{translations["loading"]}</p>
      ) : error ? (
        <p className="no-results">{error}</p>
      ) : activeUsers.length === 0 && (!showInactive || inactiveUsers.length === 0) ? (
        <p className="no-results">{translations["users.empty"]}</p>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{translations["user.info.alias"]}</th>
              <th>{translations["user.info.name"]}</th>
              <th>{translations["user.lastSeen"]}</th>
              <th>{translations["user.status"]}</th>
            </tr>
          </thead>
          <tbody>
            {activeUsers.map((user, i) => (
              <tr key={user.id}>
                <td>{i + 1}</td>
                <td>{user.alias}</td>
                <td>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}</td>
                <td>{formatSeen(user.seen)}</td>
                <td>{user.enabled === false ? translations["user.disabled"] : translations["user.enabled"]}</td>
              </tr>
            ))}
            {showInactive && inactiveUsers.map((user, i) => (
              <tr key={user.id} className="user-inactive">
                <td>{activeUsers.length + i + 1}</td>
                <td>{user.alias}</td>
                <td>{[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}</td>
                <td>{formatSeen(user.seen)}</td>
                <td>{translations["user.disabled"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UsersListPage;